// React and Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// Services/API
import { getVehicleGroupAPI } from "services/api/calculations/vehiclegroup/api";
import { initialVehicleGroupDetailForm } from "services/api/calculations/vehiclegroup/consts";
import { VehicleGroupDetailFormValues } from "services/api/calculations/vehiclegroup/types";

// Services/Validation
import { Errors } from "services/forms/validation/types";

// UI Components
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";

// Custom Components
import VehicleGroupDetailCard from "./components/VehicleGroupDetailCard/VehicleGroupDetailCard";
import VehicleGroupDeleteCard from "./components/VehicleGroupDeleteCard/VehicleGroupDeleteCard";

// Constants
import { CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP } from "app/components/Sidebar/consts";

// Types
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import {
  VehicleGroupDetailPageCmp,
  VehicleGroupDetailPageProps,
} from "./types";

// Routes
import { Routes } from "ui/modules/calculation/navigation";

const VehicleGroupDetailPage: VehicleGroupDetailPageCmp = (
  props: VehicleGroupDetailPageProps
) => {
  const { vehicleGroupId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<VehicleGroupDetailFormValues>(
    initialVehicleGroupDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (vehicleGroupId) fetchVehicleGroup(vehicleGroupId);
  }, [vehicleGroupId]);

  const fetchVehicleGroup = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleGroupDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleGroupAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP.text,
      route: SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationVehicleGroupSearchPageRouter, {
        vehicleGroupId,
      })
    );
  }, []);

  if (!vehicleGroupId) return <></>;

  return (
    <>
      <div className="bg-white border-b border-slate-200 rounded-t-md">
        <PageHeader>
          <PageTitle
            title={`${t(title.text)} [${formValues.name}]`}
            icon={title.icon}
            iconColor={title.iconColor}
          />
          <div className="flex justify-between">
            <Breadcrumb items={breadcrumbs} />
            <div className="flex gap-2">
              <Button
                size="large"
                variant="remove"
                children={t(
                  "admin.calculation.setting.vehiclegroup.detail.delete"
                )}
                onClick={() => setShowDeleteModal(true)}
                startIcon="/media/icons/trash.svg"
              />
            </div>
          </div>
        </PageHeader>
      </div>
      <div className="px-4 py-6  lg:px-4">
        <VehicleGroupDetailCard vehicleGroupId={vehicleGroupId} />
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.vehiclegroup.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <VehicleGroupDeleteCard
              vehicleGroupId={vehicleGroupId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleGroupDetailPage.route = Routes.CalculationVehicleGroupDetailPageRouter;

export default VehicleGroupDetailPage;
